// Keys of the mail client, as in HomeMail: they never fire while a person types, and Enter on a button stays a click.

import { useEffect, useRef } from 'react';
import type { Id } from '@confdeck/core-shared';
import type { ReplyMode } from './drafts.ts';
import type { Letter } from './model.ts';

export const SHORTCUTS: [string, string][] = [
  ['↓ / J', 'Следующее письмо'], ['↑ / K', 'Предыдущее письмо'], ['Esc', 'Закрыть письмо, снять выбор'], ['X', 'Выбрать письмо'],
  ['E', 'В архив'], ['# / Delete', 'В корзину'], ['!', 'В спам'], ['S', 'Важное'], ['U', 'Прочитано / не прочитано'],
  ['R', 'Ответить'], ['A', 'Ответить всем'], ['F', 'Переслать'], ['C', 'Написать'], ['/', 'Поиск'], ['Ctrl+Enter', 'Отправить письмо'], ['?', 'Все клавиши'],
];

export interface ShortcutHandlers {
  list: Letter[]; open: Letter | null; targets: Id[];
  onOpen(l: Letter): void; onClose(): void; onToggle(id: Id): void;
  onMove(ids: Id[], folder: 'archive' | 'trash' | 'junk'): void; onReply(l: Letter, mode: ReplyMode): void;
  onMark(ids: Id[], c: { unread?: boolean; starred?: boolean }): void; onCompose(): void; onSearch(): void; onHelp(): void;
}

const typing = (t: EventTarget | null) => t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
const onControl = (t: EventTarget | null) => t instanceof HTMLElement && !!t.closest('button, a[href], [role="button"], [role="menuitem"], [role="checkbox"]');

export function useShortcuts(enabled: boolean, h: ShortcutHandlers): void {
  const ref = useRef(h);
  ref.current = h;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || typing(e.target) || document.querySelector('[role="dialog"], [role="menu"]')) return;
      if ((e.key === 'Enter' || e.key === ' ') && onControl(e.target)) return;
      const { list, open, targets } = ref.current;
      const at = open ? list.findIndex((l) => l.id === open.id) : -1;
      const go = (i: number) => { const l = list[Math.max(0, Math.min(list.length - 1, i))]; if (l) ref.current.onOpen(l); };
      const first = targets.map((id) => list.find((l) => l.id === id)).find(Boolean) ?? open;
      const keys: Record<string, () => void> = {
        ArrowDown: () => go(at + 1), j: () => go(at + 1), ArrowUp: () => go(at <= 0 ? 0 : at - 1), k: () => go(at <= 0 ? 0 : at - 1),
        Enter: () => { if (!open) go(0); }, Escape: () => ref.current.onClose(),
        x: () => { if (open) ref.current.onToggle(open.id); },
        e: () => ref.current.onMove(targets, 'archive'), '#': () => ref.current.onMove(targets, 'trash'), Delete: () => ref.current.onMove(targets, 'trash'), '!': () => ref.current.onMove(targets, 'junk'),
        s: () => { if (first) ref.current.onMark(targets, { starred: !first.starred }); },
        u: () => { if (first) ref.current.onMark(targets, { unread: !first.unread }); },
        r: () => { if (open) ref.current.onReply(open, 'reply'); }, a: () => { if (open) ref.current.onReply(open, 'replyAll'); }, f: () => { if (open) ref.current.onReply(open, 'forward'); },
        c: () => ref.current.onCompose(), '/': () => ref.current.onSearch(), '?': () => ref.current.onHelp(),
      };
      const run = keys[e.key] ?? keys[e.key.toLowerCase()] ?? keys[layoutKey(e.code)];
      if (!run) return;
      e.preventDefault();
      run();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}

/** The Russian layout gives «о» for J: the letter keys work by their place on the keyboard as well. */
const layoutKey = (code: string) => (code === 'Slash' ? '/' : /^Key[A-Z]$/.test(code) ? code.slice(3).toLowerCase() : '');
